import { useCallback, useRef, useState } from 'react';

import {
  type EmbeddedPaymentElementConfiguration,
  type EmbeddedPaymentElementResult,
  type IntentConfiguration,
} from '@stripe/stripe-react-native';

import { OrderPaymentStatus } from '@sg/graphql-schema';
import { OrderTypeValues } from '@sg/shared-be/features/ordering';
import { PaymentState } from '@sg/shared-fe/features/ordering';

import { PaymentMethodType, storePendingPaymentInfo } from '@order/features/ordering';
import {
  currencyCode,
  getElementConfig,
  paymentMethodTypes,
  paymentMethodTypesWithCashApp,
  setupFutureUsage,
  useNotifyStripeCancel,
  useNotifyStripeError,
  useStripePayment,
} from '@order/integrations/stripe';
import { useTelemetry } from '@order/Telemetry';

import { type GiftCardCheckoutFormReturn } from '../../form';
import { useGiftCardPaymentIntent } from '../../hooks';
import { useGiftCardPaymentInfo } from '../useGiftCardPaymentInfo';

// ─────────────────────────────────────────────────────────────────────────────

export const useGiftCardStripeIntegration = (props: UseGiftCardStripeIntegrationProps) => {
  const {
    amount,
    form,
    clearPaymentState,
    failPayment,
    paymentMethods,
    requestFormSubmission,
    startPolling,
    createPayment,
    cancelPayment,
  } = props;

  const assetId = form.getValues('assetId');
  const orderIdRef = useRef<string | undefined>(undefined);

  const { stripeCustomerId, stripeCustomerKey } = useGiftCardPaymentInfo();
  const createGiftCardPaymentIntent = useGiftCardPaymentIntent();

  const { track } = useTelemetry();
  const trackStripeUsage = useCallback(
    (payload: unknown) => {
      track('gift-card.stripe-usage', payload);
    },
    [track],
  );

  const stripeElementConfigRef = useRef<EmbeddedPaymentElementConfiguration>(
    getElementConfig({
      returnURL: `sweetgreen-order://gift/checkout/${assetId}`,
      stripeCustomerId,
      stripeCustomerKey,
    }),
  );

  const stripeIntentConfigRef = useRef<IntentConfiguration>({
    paymentMethodTypes: startPolling ? paymentMethodTypesWithCashApp : paymentMethodTypes,
    mode: startPolling ? { amount, currencyCode } : { amount, currencyCode, setupFutureUsage },
    confirmHandler: async (paymentResult, shouldSetupFutureUsage, intentCreationCallback) => {
      if (!startPolling) {
        trackStripeUsage({ event: 'stripe_success' });
        intentCreationCallback(SUCCESS_COMPLETE);
        await requestFormSubmission(paymentResult.id);
        return;
      }

      try {
        const assetId = form.getValues('assetId');

        const { orderId, paymentIntentId, clientSecret } = await createGiftCardPaymentIntent({
          shouldSetupFutureUsage,
          assetId,
          paymentMethodId: paymentResult.id,
          balance: form.getValues('amount'),
          deliveryDateTime: form.getValues('deliveryDateTime'),
          deliveryMethods: [form.getValues('deliveryMethod')],
          isAsap: form.getValues('deliveryDateOption') === 'NOW',
          message: form.getValues('message'),
          quantity: form.getValues('quantity'),
          recipientEmail: form.getValues('recipientEmail'),
          recipientName: form.getValues('recipientName'),
          senderName: form.getValues('senderName'),
          utcOffset: form.getValues('utcOffset') ?? 0,
        });

        // Stores the resulting order id and payment intent id for after the confirmation.
        orderIdRef.current = orderId;

        createPayment?.({
          paymentIntentId,
          paymentStateExpiration: Date.now(),
          paymentStateStatus: OrderPaymentStatus.Created,
          paymentStateError: undefined,
        });

        // We need to set the order id in local storage after payment intent creation.
        // This will be used to show gift card fulfillment feedback from the home screen.
        await storePendingPaymentInfo(orderId, OrderTypeValues.GiftCardOrder, assetId);

        if (clientSecret) intentCreationCallback({ clientSecret });
      } catch (error) {
        intentCreationCallback({
          error: { code: 'Failed', message: (error as Error).message },
        });
      }
    },
  });

  const { embeddedPaymentElementView, selectedPaymentMethod, confirmStripe, isUpdatingTotal } =
    useStripePayment({
      isPaymentStatePollingEnabled: Boolean(startPolling),
      total: amount,
      paymentMethods,
      stripeIntentConfigRef,
      stripeElementConfigRef,
    });

  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const notifyStripeError = useNotifyStripeError(trackStripeUsage);
  const notifyStripeCancel = useNotifyStripeCancel();

  const confirmPayment = useCallback(async () => {
    if (!selectedPaymentMethod && paymentMethods.length > 0) {
      trackStripeUsage({ event: 'confirming_with_fallback' });
      await requestFormSubmission(paymentMethods[0].id);

      return;
    }

    if (!selectedPaymentMethod) {
      trackStripeUsage({ event: 'confirming_without_payment_option' });
    }

    setIsConfirmingPayment(true);
    clearPaymentState?.();

    const result = (await confirmStripe()) as EmbeddedPaymentElementResult & {
      paymentMethodId: string;
    };

    setIsConfirmingPayment(false);

    // Start polling the gift card payment state when the payment intent is confirmed.
    if (result.status === 'completed' && startPolling && orderIdRef.current) {
      trackStripeUsage({ event: 'result_completed', result });
      startPolling(orderIdRef.current);
      return;
    }

    // Start gift card submission when the payment method is available.
    if (result.status === 'completed' && result.paymentMethodId && !startPolling) {
      trackStripeUsage({ event: 'result_completed', result });
      await requestFormSubmission(result.paymentMethodId);
      return;
    }

    if (result.status === 'failed') {
      trackStripeUsage({ event: 'result_failed', error: result.error });
      notifyStripeError(result.error.message ?? result.error);
      failPayment();
      return;
    }

    if (result.status === 'canceled') {
      trackStripeUsage({ event: 'result_canceled' });
      notifyStripeCancel();
      cancelPayment?.();
    }
  }, [
    setIsConfirmingPayment,
    selectedPaymentMethod,
    paymentMethods,
    confirmStripe,
    trackStripeUsage,
    startPolling,
    requestFormSubmission,
    notifyStripeError,
    clearPaymentState,
    failPayment,
    notifyStripeCancel,
    cancelPayment,
  ]);

  return {
    isConfirmingPayment,
    isUpdatingTotal,
    embeddedPaymentElementView,
    confirmPayment,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseGiftCardStripeIntegrationProps = {
  amount: number;
  form: GiftCardCheckoutFormReturn;
  paymentMethods: readonly PaymentMethod[];
  createPayment?: (paymentState: PaymentState) => void;
  cancelPayment?: () => void;
  clearPaymentState?: () => void;
  failPayment: () => void;
  startPolling?: (orderId: string) => void;
  requestFormSubmission: (paymentMethodId: string) => Promise<void>;
};

type PaymentMethod = {
  id: string;
  description: string;
  nickname: string;
  cardType: PaymentMethodType;
  isDefault: boolean;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const NO_INTENT_COMPLETE = 'COMPLETE_WITHOUT_CONFIRMING_INTENT';
const SUCCESS_COMPLETE = { clientSecret: NO_INTENT_COMPLETE };
